/** @jsxImportSource react */
import { Alert, Box, Button, Chip, IconButton, Paper, Stack, Tooltip, Typography } from '@mui/material'
import DragIndicatorIcon from '@mui/icons-material/DragIndicator'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward'
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward'
import AddIcon from '@mui/icons-material/Add'
import { useDroppable } from '@dnd-kit/core'
import { SortableContext, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import FieldTypeIcon from './FieldTypeIcon'
import { fieldTypeLabel } from './builderModel'

export const SECTION_PREFIX = 'section:'

/** Visual for a field, shared by the sortable card and the drag overlay. */
export const FieldCardBody = ({ field, selected, hasError, dragHandle, onRemove, dragging }) => (
  <Box
    sx={{
      display: 'flex', alignItems: 'center', gap: 1, p: 1, pr: 0.5, borderRadius: 1, border: 1,
      borderColor: hasError ? 'error.main' : selected ? 'primary.main' : 'divider',
      bgcolor: 'background.paper',
      boxShadow: dragging ? 6 : selected ? (t) => `0 0 0 1px ${t.palette.primary.main}` : 'none',
      cursor: 'pointer',
    }}
  >
    {dragHandle}
    <FieldTypeIcon type={field.fieldType} color="action" />
    <Box sx={{ minWidth: 0, flexGrow: 1 }}>
      <Typography variant="body2" noWrap sx={{ fontWeight: 500 }}>
        {field.label || <em>Untitled</em>}
        {field.required && <Box component="span" sx={{ color: 'error.main', ml: 0.5 }}>*</Box>}
      </Typography>
      <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block' }}>
        {fieldTypeLabel(field.fieldType)} · <Box component="span" sx={{ fontFamily: 'monospace' }}>{field.fieldKey}</Box>
      </Typography>
    </Box>
    <Stack direction="row" spacing={0.5} sx={{ display: { xs: 'none', sm: 'flex' } }}>
      {field.readOnly && <Chip label="Read-only" variant="outlined" />}
      {field.hidden && <Chip label="Hidden" variant="outlined" />}
    </Stack>
    {onRemove && (
      <Tooltip title="Remove field">
        <IconButton onClick={(e) => { e.stopPropagation(); onRemove() }} aria-label={`Remove ${field.label}`}>
          <DeleteOutlineIcon fontSize="small" />
        </IconButton>
      </Tooltip>
    )}
  </Box>
)

const SortableField = ({ field, selected, hasError, readOnly, onSelect, onRemove }) => {
  const { attributes, listeners, setNodeRef, setActivatorNodeRef, transform, transition, isDragging } =
    useSortable({ id: field.uid, disabled: readOnly })
  return (
    <Box
      ref={setNodeRef}
      onClick={onSelect}
      sx={{ transform: CSS.Translate.toString(transform), transition, opacity: isDragging ? 0.35 : 1 }}
    >
      <FieldCardBody
        field={field}
        selected={selected}
        hasError={hasError}
        onRemove={readOnly ? null : onRemove}
        dragHandle={!readOnly && (
          <Box
            ref={setActivatorNodeRef}
            {...listeners}
            {...attributes}
            aria-label={`Drag ${field.label}`}
            sx={{ display: 'flex', cursor: 'grab', color: 'text.secondary', touchAction: 'none' }}
          >
            <DragIndicatorIcon fontSize="small" />
          </Box>
        )}
      />
    </Box>
  )
}

const SectionCard = ({ section, index, count, state, errors, readOnly, dispatch }) => {
  const { setNodeRef, isOver } = useDroppable({ id: `${SECTION_PREFIX}${section.uid}`, disabled: readOnly })
  const selected = state.selected.kind === 'section' && state.selected.uid === section.uid
  const sectionErrors = errors.items[section.uid]

  return (
    <Paper
      variant="outlined"
      onClick={() => dispatch({ type: 'select', selected: { kind: 'section', uid: section.uid } })}
      sx={{ p: 1.5, borderColor: sectionErrors ? 'error.main' : selected ? 'primary.main' : 'divider', cursor: 'pointer' }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
        <Box sx={{ flexGrow: 1, minWidth: 0 }}>
          <Typography variant="subtitle2" noWrap>{section.title || <em>Untitled section</em>}</Typography>
          <Typography variant="caption" color="text.secondary">
            {section.fields.length} {section.fields.length === 1 ? 'field' : 'fields'} · {section.columns} {section.columns === 1 ? 'column' : 'columns'}
          </Typography>
        </Box>
        {!readOnly && (
          <Box onClick={(e) => e.stopPropagation()}>
            <Tooltip title="Move up"><span><IconButton disabled={index === 0} onClick={() => dispatch({ type: 'moveSection', uid: section.uid, delta: -1 })} aria-label="Move section up"><ArrowUpwardIcon fontSize="small" /></IconButton></span></Tooltip>
            <Tooltip title="Move down"><span><IconButton disabled={index === count - 1} onClick={() => dispatch({ type: 'moveSection', uid: section.uid, delta: 1 })} aria-label="Move section down"><ArrowDownwardIcon fontSize="small" /></IconButton></span></Tooltip>
            <Tooltip title="Remove section"><span><IconButton disabled={count === 1} onClick={() => dispatch({ type: 'removeSection', uid: section.uid })} aria-label="Remove section"><DeleteOutlineIcon fontSize="small" /></IconButton></span></Tooltip>
          </Box>
        )}
      </Box>
      {sectionErrors && <Alert severity="error" sx={{ mb: 1, py: 0 }}>{sectionErrors.join(' · ')}</Alert>}

      <SortableContext items={section.fields.map((f) => f.uid)} strategy={verticalListSortingStrategy}>
        <Box
          ref={setNodeRef}
          sx={{
            display: 'flex', flexDirection: 'column', gap: 0.75, minHeight: 56, p: 0.75, borderRadius: 1,
            border: '1px dashed', borderColor: isOver ? 'primary.main' : 'transparent',
            bgcolor: isOver ? 'action.hover' : 'transparent', transition: 'background-color .15s',
          }}
        >
          {section.fields.map((field) => (
            <SortableField
              key={field.uid}
              field={field}
              readOnly={readOnly}
              selected={state.selected.kind === 'field' && state.selected.uid === field.uid}
              hasError={!!errors.items[field.uid]}
              onSelect={(e) => { e.stopPropagation(); dispatch({ type: 'select', selected: { kind: 'field', uid: field.uid } }) }}
              onRemove={() => dispatch({ type: 'removeField', uid: field.uid })}
            />
          ))}
          {section.fields.length === 0 && (
            <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 1.5 }}>
              {readOnly ? 'No fields' : 'Drop fields here'}
            </Typography>
          )}
        </Box>
      </SortableContext>
    </Paper>
  )
}

const BuilderCanvas = ({ state, errors, readOnly, dispatch }) => (
  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
    <Paper
      variant="outlined"
      onClick={() => dispatch({ type: 'select', selected: { kind: 'form' } })}
      sx={{ p: 1.5, cursor: 'pointer', borderColor: state.selected.kind === 'form' ? 'primary.main' : Object.keys(errors.meta).length ? 'error.main' : 'divider' }}
    >
      <Typography variant="h6" noWrap>{state.meta.name || <em>Untitled form</em>}</Typography>
      <Typography variant="caption" color="text.secondary">
        {state.meta.entityType || 'ENTITY'} · <Box component="span" sx={{ fontFamily: 'monospace' }}>{state.meta.code || 'form-code'}</Box>
      </Typography>
    </Paper>
    {state.sections.map((section, index) => (
      <SectionCard key={section.uid} section={section} index={index} count={state.sections.length} state={state} errors={errors} readOnly={readOnly} dispatch={dispatch} />
    ))}
    {!readOnly && (
      <Button startIcon={<AddIcon />} onClick={() => dispatch({ type: 'addSection' })} sx={{ alignSelf: 'flex-start' }}>
        Add section
      </Button>
    )}
  </Box>
)

export default BuilderCanvas
